import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '#/shared/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from '#/shared/ui/dialog'
import { CloseIcon } from '#/shared/ui/icons'
import { Textarea } from '#/shared/ui/textarea'

import { usePaymentComment } from '../model/use-payment-comment'

const NOTE_MAX_LENGTH = 250

interface PaymentCommentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  paymentId: string
}

function CommentHeader() {
  const { t } = useTranslation()
  return (
    <header className="flex h-13 shrink-0 items-center justify-between gap-2.5 border-b border-gray-200 px-5 py-2.5">
      <DialogTitle className="truncate text-lg font-semibold text-gray-900">
        {t('orderDetails.comment.payment.commentDialog.title')}
      </DialogTitle>
      <DialogClose
        aria-label={t('orderDetails.comment.payment.detail.close')}
        className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-50 focus-visible:outline-none focus-visible:focus-ring"
      >
        <CloseIcon className="size-3.5" />
      </DialogClose>
    </header>
  )
}

function CommentBody({
  value,
  disabled,
  onChange,
}: {
  value: string
  disabled: boolean
  onChange: (next: string) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto px-5 py-4">
      <Textarea
        value={value}
        maxLength={NOTE_MAX_LENGTH}
        placeholder={t(
          'orderDetails.comment.payment.commentDialog.placeholder',
        )}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value.slice(0, NOTE_MAX_LENGTH))}
      />
      <div className="text-right text-xs text-gray-400 select-none">
        {value.length}/{NOTE_MAX_LENGTH}
      </div>
    </div>
  )
}

export function PaymentCommentDialog({
  open,
  onOpenChange,
  paymentId,
}: PaymentCommentDialogProps) {
  const { t } = useTranslation()
  const comment = usePaymentComment(paymentId, open)
  const [value, setValue] = useState('')

  // Prefill from the fetched detail each time the dialog opens.
  useEffect(() => {
    if (open) setValue(comment.note)
  }, [open, comment.note])

  const handleSave = async () => {
    const saved = await comment.save(value.trim())
    if (saved) onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[420px]">
        <CommentHeader />
        <CommentBody
          value={value}
          disabled={comment.isLoading || comment.isSaving}
          onChange={setValue}
        />
        <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <DialogClose asChild>
            <Button
              type="button"
              variant="outline"
              size="xs"
              pressEffect
              className="px-3.75"
              disabled={comment.isSaving}
            >
              {t('orderDetails.comment.payment.commentDialog.cancel')}
            </Button>
          </DialogClose>
          <Button
            type="button"
            size="xs"
            pressEffect
            className="px-3.75"
            disabled={comment.isLoading || comment.isSaving}
            onClick={() => void handleSave()}
          >
            {comment.isSaving
              ? t('table.saving')
              : t('orderDetails.comment.payment.commentDialog.save')}
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  )
}
